import { AnimatePresence, motion } from "framer-motion";
import ToolTip from "components/design/toolTip";
import { DottedCircle } from "components/icons/DottedCircle";
import { GreentickGreenCircle } from "components/icons/GreentickGreenCircle";
import { Info } from "components/icons/Info";
import { RecentDocument, RequiredItem } from "v2/store/entityDocumentsApi";
import { groupRequiredBySection } from "./groupRequiredBySection";
import { UploadedDocumentRow } from "./UploadedDocumentRow";

type SupportingDocumentsListProps = {
  entityId: string;
  items: RequiredItem[];
  matchedIds: Set<string>;

  uploaded: RecentDocument[];
};

type SupportingDocumentRowProps = {
  item: RequiredItem;
  isMatched: boolean;
};

const StatusIcon = ({ isMatched }: { isMatched: boolean }) => {
  if (isMatched) {
    return (
      <GreentickGreenCircle
        width="14"
        height="14"
        className="t-shrink-0 t-text-dark_green"
      />
    );
  }

  return <DottedCircle size="14" className="t-shrink-0 t-text-purple-30" />;
};

const SupportingDocumentRow = ({
  item,
  isMatched,
}: SupportingDocumentRowProps) => (
  <div className="t-flex t-items-center t-gap-2 t-rounded-lg t-bg-surface">
    <StatusIcon isMatched={isMatched} />

    <span className="t-flex t-min-w-0 t-items-center t-gap-1">
      <span className="t-truncate t-text-body t-text-text-60">
        {item.label}
      </span>

      {item.description && (
        <ToolTip text={item.description}>
          <span className="t-flex t-shrink-0 t-text-text-30">
            <Info size="14" />
          </span>
        </ToolTip>
      )}
    </span>
  </div>
);

export const SupportingDocumentsList = ({
  entityId,
  items,
  matchedIds,
  uploaded,
}: SupportingDocumentsListProps) => {
  const groups = groupRequiredBySection(items, uploaded);

  return (
    <>
      {groups.map((group) => (
        <div key={group.section} className="t-flex t-items-center">
          <span className="t-w-4 t-shrink-0" aria-hidden />

          <div className="t-ml-1.5 t-flex t-min-w-0 t-flex-1 t-flex-col t-gap-3">
            <span className="t-text-caption t-text-neutral-40">
              {group.section}
            </span>

            {group.items.length > 0 && (
              <ul className="t-my-0 t-flex t-list-none t-flex-col t-gap-3 t-pl-0">
                <AnimatePresence initial={false}>
                  {group.items.map((item) => (
                    <motion.li
                      key={item.id}
                      layout
                      exit={{ x: 64, opacity: 0 }}
                      transition={{ duration: 0.35, ease: "easeInOut" }}
                    >
                      <SupportingDocumentRow
                        item={item}
                        isMatched={matchedIds.has(item.id)}
                      />
                    </motion.li>
                  ))}
                </AnimatePresence>
              </ul>
            )}

            {group.uploaded.length > 0 && (
              <div className="t-flex t-flex-col">
                {group.uploaded.map((document) => (
                  <UploadedDocumentRow
                    key={document.id}
                    document={document}
                    entityId={entityId}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      ))}
    </>
  );
};
